/**
 * ProductDetails.ImageGallery.View.Ext.js — LEGACY
 *
 * Modern PDP gallery — CSS scroll-snap. Images follow your finger
 * during swipe and snap into place (Apple-style UX).
 *
 * All product images rendered as slides in a horizontal scroll track.
 * IntersectionObserver detects active slide and syncs thumbnails/dots.
 * Desktop: arrow click + lightbox. Mobile: native swipe + dots.
 * YouTube video support via facade pattern (lazy iframe).
 *
 * No jQuery plugins. No bxSlider. No src-swap.
 * Dependencies: Backbone, underscore, Utilities.ResizeImage.
 *
 * Overrides: template, initialize, getContext, initSlider, initZoom.
 * No Extensibility API equivalent — prototype method overrides required.
 */
define("ProductDetails.ImageGallery.View.Ext", [
  "ProductDetails.ImageGallery.View",
  "Backbone.CompositeView",
  "Utilities.ResizeImage",
  "SocialSharing.Flyout.Hover.View",
  "SC.Configuration",

  "speedmastercore_image_gallery.tpl",

  "Backbone",
  "underscore",
  "Utils"
], function (
  View,
  BackboneCompositeView,
  resizeImage,
  SocialSharingFlyoutHoverView,
  Configuration,

  speedmastercore_image_gallery_tpl,

  Backbone,
  _,
  Utils
) {
  "use strict";

  // Resolve the actual prototype (handles wrapped module exports)
  var GalleryProto = (View.ProductDetailsImageGalleryView &&
    View.ProductDetailsImageGalleryView.prototype) || View.prototype;

  /**
   * Extract YouTube video ID from any common URL format.
   * Handles: youtube.com/watch?v=, youtube.com/embed/, youtu.be/, youtube.com/shorts/
   * @param {string} url
   * @returns {string|null} 11-char video ID or null
   */
  function extractVideoId(url) {
    if (!url) { return null; }
    var match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  }

  _.extend(GalleryProto, {

    template: speedmastercore_image_gallery_tpl,

    /**
     * Wrap initialize — extract video data separately from images.
     * Video is NOT in the images array. Stored as this.videoData.
     */
    initialize: _.wrap(GalleryProto.initialize, function(fn) {
      fn.apply(this, _.toArray(arguments).slice(1));

      this.videoData = null;

      var item = this.model && this.model.get('item');
      var videoRawUrl = item
        ? (typeof item.get === 'function' ? item.get('custitem_video_url') : item.custitem_video_url)
        : '';

      if (videoRawUrl) {
        var videoId = extractVideoId(videoRawUrl);

        if (videoId) {
          this.videoData = {
            // Privacy-enhanced mode, no deprecated params
            embedUrl: 'https://www.youtube-nocookie.com/embed/' + videoId + '?controls=1&rel=0',
            thumbUrl: 'https://img.youtube.com/vi/' + videoId + '/hqdefault.jpg',
            videoId: videoId
          };
        }
      }
    }),

    /**
     * Wrap getContext — add video and arrow context variables.
     * Arrows show when total slides (images + video) > 1.
     */
    getContext: _.wrap(GalleryProto.getContext, function(fn) {
      var context = fn.apply(this, _.toArray(arguments).slice(1));

      // Cache resize ID for JS use
      this._imageResizeId = context.imageResizeId || 'main';

      // Video context (facade pattern — iframe loads only on click)
      context.hasVideo = !!this.videoData;
      context.videoEmbedUrl = this.videoData ? this.videoData.embedUrl : '';
      context.videoThumbUrl = this.videoData ? this.videoData.thumbUrl : '';

      // Show thumbs when multiple images OR has video
      context.showImageSlider = context.images.length > 1 || !!this.videoData;

      // Show arrows when total slides > 1 (images + optional video)
      var totalSlides = context.images.length + (this.videoData ? 1 : 0);
      context.showArrows = totalSlides > 1;

      return context;
    }),

    /**
     * Gallery initialization — CSS scroll-snap.
     * Called by the view's afterRender.
     *
     * Features:
     * - Horizontal scroll track with all slides
     * - CSS scroll-snap-type: x mandatory — native finger-following physics
     * - IntersectionObserver syncs active thumbnail/dot to visible slide
     * - Thumbnail click → scrollTo slide (smooth)
     * - Arrow click → scrollTo next/prev (smooth, wraps around)
     * - Keyboard → left/right arrow keys when stage focused
     * - Video facade → lazy-load YouTube iframe on click
     * - Video auto-play muted on landing (browser policy)
     * - Video stops when swiping away (iframe removed)
     */
    initSlider: function initSlider() {
      var self = this;
      var images = this.images;
      var videoData = this.videoData;

      // Cache DOM
      var $stage = this.$('[data-gallery-stage]');
      var $track = this.$('[data-gallery-track]');
      var trackEl = $track[0];
      var $slides = this.$('[data-gallery-slide]');
      var slides = $slides.toArray();
      var $thumbs = this.$('[data-gallery-thumb]');
      var $prevBtn = this.$('[data-gallery-prev]');
      var $nextBtn = this.$('[data-gallery-next]');
      var $facade = this.$('[data-gallery-facade]');
      var $videoWrap = this.$('[data-gallery-video]');

      if (!trackEl || slides.length === 0) { return; }

      var currentSlideIdx = 0;
      var slideCount = slides.length;

      // Expose initial state for lightbox integration
      self._galleryIndex = 0;
      self._isVideoActive = !!videoData;

      // ── Scroll to a specific slide ───────────────────────

      function scrollToSlide(idx, smooth) {
        if (idx < 0 || idx >= slideCount) { return; }
        var slide = slides[idx];
        trackEl.scrollTo({
          left: slide.offsetLeft,
          behavior: smooth ? 'smooth' : 'auto'
        });
      }

      // ── Detect active slide via IntersectionObserver ─────
      // Fires when a slide crosses 50% visibility within the
      // scroll track. Updates thumbnails/dots + internal state.

      function onSlideVisible(slideEl) {
        var dataIdx = slideEl.getAttribute('data-index');
        currentSlideIdx = slides.indexOf(slideEl);

        if (dataIdx === 'video') {
          self._isVideoActive = true;
          self._galleryIndex = 0;
        } else {
          var imgIdx = parseInt(dataIdx, 10);
          self._isVideoActive = false;
          self._galleryIndex = imgIdx;

          // Stop video when leaving video slide
          if (videoData && $videoWrap.length) {
            $videoWrap.empty().hide();
            $facade.show();
          }
        }

        // Update active thumbnail/dot
        $thumbs.removeClass('active');
        $thumbs.filter('[data-index="' + dataIdx + '"]').addClass('active');
      }

      if (typeof IntersectionObserver !== 'undefined') {
        var observer = new IntersectionObserver(function(entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && entries[i].intersectionRatio >= 0.5) {
              onSlideVisible(entries[i].target);
            }
          }
        }, {
          root: trackEl,
          threshold: 0.5
        });

        for (var s = 0; s < slides.length; s++) {
          observer.observe(slides[s]);
        }
      }

      // ── Navigate (wraps around) ─────────────────────────

      function goNext() {
        var next = currentSlideIdx + 1;
        if (next >= slideCount) { next = 0; }
        scrollToSlide(next, true);
      }

      function goPrev() {
        var prev = currentSlideIdx - 1;
        if (prev < 0) { prev = slideCount - 1; }
        scrollToSlide(prev, true);
      }

      // ── Thumbnail clicks ────────────────────────────────

      $thumbs.on('click', function() {
        var idx = this.getAttribute('data-index');
        for (var t = 0; t < slides.length; t++) {
          if (slides[t].getAttribute('data-index') === idx) {
            scrollToSlide(t, true);
            break;
          }
        }
      });

      // ── Arrow clicks ────────────────────────────────────

      $prevBtn.on('click', function(e) { e.preventDefault(); goPrev(); });
      $nextBtn.on('click', function(e) { e.preventDefault(); goNext(); });

      // ── Video facade click → play (unmuted, user-initiated) ──

      $facade.on('click', function() {
        if (!videoData) { return; }
        self._isVideoActive = true;

        $facade.hide();
        $videoWrap
          .html(
            '<div class="sm-gallery-video-inner">' +
              '<iframe src="' + videoData.embedUrl + '&autoplay=1" ' +
                'allowfullscreen ' +
                'allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ' +
                'title="Product video">' +
              '</iframe>' +
            '</div>'
          )
          .show();
      });

      // ── Keyboard navigation ─────────────────────────────

      $stage.on('keydown', function(e) {
        if (e.keyCode === 37) { e.preventDefault(); goPrev(); }  // Left arrow
        if (e.keyCode === 39) { e.preventDefault(); goNext(); }  // Right arrow
      });

      // ── Auto-play video on landing ──────────────────────
      // Premium UX: when video exists, play muted immediately
      // (browser autoplay policy requires mute). Facade serves
      // as brief loading placeholder / click-to-unmute fallback.

      if (videoData) {
        setTimeout(function() {
          $facade.hide();
          $videoWrap
            .html(
              '<div class="sm-gallery-video-inner">' +
                '<iframe src="' + videoData.embedUrl + '&autoplay=1&mute=1" ' +
                  'allowfullscreen ' +
                  'allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ' +
                  'title="Product video">' +
                '</iframe>' +
              '</div>'
            )
            .show();
        }, 100);
      }
    },

    /**
     * Fullscreen lightbox — replaces SC's jQuery.zoom.
     * Desktop only — click on gallery image opens fullscreen overlay.
     * Mobile: no-op (native pinch-spread suffices).
     *
     * Called by SC when "Enable Zoom for Images" is checked in
     * Commerce config (custpage_field_is_zoom_enabled).
     * Lightbox.open() checks viewport width internally.
     */
    initZoom: function initZoom() {
      var self = this;
      var Lightbox = require('SpeedmasterCore.Lightbox');
      var $stage = this.$('[data-gallery-stage]');

      if (!$stage.length) { return; }

      $stage.on('click', function(e) {
        // Only open lightbox when clicking on a product image (not video, arrows, etc.)
        if (!e.target.closest || !e.target.closest('.sm-gallery-image')) { return; }

        Lightbox.open(
          self.images,
          null, // no video in lightbox — gallery handles video inline
          self._galleryIndex || 0,
          false,
          $stage[0]
        );
      });
    }
  });
});
